/**
 *  CombinedChart script file
**/
import * as d3 from 'd3'
import customFormatters from '@/filters/customFormatters'

export default {
  name: 'CombinedChart',
  mixins: [customFormatters],
  components: {},
  props: {
    y1Data: {
      type: Object,
      required: false
    },
    y2Data: {
      type: Object,
      required: false
    },
    margin: {
      type: Object,
      default: () => ({ top: 20, right: 60, bottom: 20, left: 60 }),
      required: false
    },
    colors: {
      type: Array,
      default: () => ['#F23063', '#11a579', '#3969ac', '#f2b701', '#e73f74'],
      required: false
    },
    ticks: {
      type: Number,
      default: 4,
      required: false
    },
    tickValues: {
      type: Array,
      default: undefined,
      required: false
    },
    barWidth: {
      type: Number,
      default: 20,
      required: false
    },
    barsOpacity: {
      type: Number,
      default: 0.4,
      required: false
    },
    legend: {
      type: Boolean,
      default: false,
      required: false
    },
    labelLeft: {
      type: String,
      default: undefined,
      required: false
    },
    labelRight: {
      type: String,
      default: undefined,
      required: false
    },
    xAxisWithDays: {
      type: Boolean,
      default: true
    },
    maxXTicks: {
      type: Number,
      default: 20
    }
  },
  data () {
    return {
      max: 0,
      max1: 0,
      max2: 0,
      bars: [],
      lines: [],
      data: null,
      width: 0,
      height: 0,
      keys: null,
      xScaleGroup: null,
      xScaleBar: null,
      y1Scale: null,
      y2Scale: null,
      g: null,
      colorScale: null,
      groupwidth: 0,
      legendItems: [],
      tooltipData: {
        key: '',
        value: ''
      }
    }
  },
  mounted () {
    this.bars = [
      ...(this.y1Data && this.y1Data.type === 'bar') ? this.y1Data.data : [],
      ...(this.y2Data && this.y2Data.type === 'bar') ? this.y2Data.data : []
    ]

    this.lines = [
      ...(this.y1Data && this.y1Data.type === 'line') ? this.y1Data.data : [],
      ...(this.y2Data && this.y2Data.type === 'line') ? this.y2Data.data : []
    ]

    this.data = this.bars.length ? this.bars : this.lines
    this.keys = Array.from(new Set(this.data.flatMap(d => d.values.map(v => v.key))))

    this.max1 = d3.max(this.y1Data.data || [], d => d3.max(d.values || [], d => d.value))

    if (this.y2Data) {
      this.max2 = d3.max(this.y2Data.data || [], d => d3.max(d.values || [], d => d.value))
    }
    this.max = Math.max(this.max1, this.max2)

    this.colorScale = d3.scaleOrdinal().range(this.keys.length > 1 ? this.colors : ['#16969d'])
    this.groupwidth = this.barWidth + this.keys.length

    this.drawChart()
  },
  computed: {
  },
  methods: {
    dateFormatter (date) {
      date = (date instanceof Date ? date : new Date(date))
      const label = date.toLocaleDateString('es-ES', {
        month: 'short',
        day: this.xAxisWithDays ? 'numeric' : undefined
      }).replace('.', '')
      return customFormatters.filters.titleCase(label)
    },
    drawChart () {
      const svgWidthBounding = this.$refs.svg.getBoundingClientRect()
      this.width = svgWidthBounding.width - (this.margin.left + this.margin.right)
      this.height = svgWidthBounding.height - (this.margin.top + this.margin.bottom)

      const svg = d3.select(this.$refs.svg)
        .attr('width', `${svgWidthBounding.width}px`)
        .attr('height', `${svgWidthBounding.height}px`)

      this.g = svg.append('g')
        .attr('transform', `translate(0, ${this.margin.top})`)

      this.xScaleGroup = d3.scaleBand()
        .rangeRound([0, this.width])
        .paddingInner(0.2)
        .domain(this.data.map(d => d.date))

      this.xScaleBar = d3.scaleBand()
        .paddingInner(0.1)
        .domain(this.keys)
        .rangeRound([0, this.groupwidth])

      this.yScale1 = d3.scaleLinear()
        .rangeRound([this.height, 0])
        .domain([0, this.max1 || 1])

      if (this.y2Data) {
        this.yScale2 = d3.scaleLinear()
          .rangeRound([this.height, 0])
          .domain([0, this.max2 || 1])
      }

      this.drawAxis()
      this.bars && this.drawBars()
      this.lines && this.drawLines()

      this.legend && this.drawLegend()
    },
    drawAxis () {
      this.g.append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .call(
          d3.axisLeft(this.yScale1)
            .ticks(this.ticks)
            .tickSize(-(this.width))
        )

      this.g.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(${this.margin.left}, ${this.height})`)
        .call(
          d3.axisBottom(this.xScaleGroup)
            .tickValues(this.generateXTickValues())
            .tickFormat(this.dateFormatter)
        )

      const that = this
      const guideLines = this.g.append('g')
        .attr('class', 'guide-lines')
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .selectAll('.guide-line')
        .data(this.data).enter()
        .append('g')
        .on('mouseover', function (d) { that.showTooltip(d, that.xScaleGroup, this) })
        .on('mouseout', function (d) { that.hideTooltip(that.xScaleGroup(d.date), this) })

      guideLines.append('rect')
        .attr('class', 'guide')
        .attr('x', d => this.xScaleGroup(d.date))
        .attr('width', `${this.xScaleGroup.bandwidth()}px`)
        .attr('y', d => 0)
        .attr('height', d => this.yScale1(0))
        .attr('fill', 'transparent')

      guideLines.append('line')
        .attr('class', 'guide-line')
        .attr('transform', `translate(${this.xScaleGroup.bandwidth() / 2}, 0)`)
        .attr('x1', d => this.xScaleGroup(d.date))
        .attr('x2', d => this.xScaleGroup(d.date))
        .attr('y1', d => 0)
        .attr('y2', d => this.yScale1(0))
        .attr('height', d => this.yScale1(0))

      this.g.append('g')
        .attr('class', 'y-axis')
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .call(
          d3.axisLeft(this.yScale1)
            .ticks(this.ticks)
        )

      if (this.labelRight && this.y2Data) {
        this.g.append('g')
          .attr('class', 'y-axis')
          .attr('transform', `translate(${this.width + this.margin.right}, 0)`)
          .call(
            d3.axisRight(this.yScale2)
              .ticks(this.ticks)
          )
      }

      d3.selectAll('.grid .tick line').style('stroke', '#e4e5e8')
      d3.selectAll('.grid .tick text').style('display', 'none')
      d3.selectAll('path.domain').style('display', 'none')
      d3.selectAll('.x-axis .tick line').style('display', 'none')
      d3.selectAll('.y-axis .tick line').style('display', 'none')
    },
    generateXTickValues () {
      const domain = this.xScaleGroup.domain()
      return domain.length > this.maxXTicks
        ? domain.filter((d, i) => i % 2 === 0)
        : domain
    },
    drawBars () {
      const rad = this.keys.length > 1 ? 1 : 3
      let bars = []
      let scale

      if (this.y1Data && this.y1Data.type === 'bar') {
        bars = this.y1Data.data
        scale = this.yScale1
      } else if (this.y2Data && this.y2Data.type === 'bar') {
        bars = this.y2Data.data
        scale = this.yScale2
      } else {
        return
      }

      this.g.append('g')
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .attr('class', 'bars')
        .style('pointer-events', 'none')
        .selectAll('g')
        .data(bars)
        .enter().append('g')
        .attr('class', 'bar')
        .attr('transform', d => `translate(${this.xScaleGroup(d.date) + ((this.xScaleGroup.bandwidth() / 2) - (this.groupwidth / 2))}, 0)`)
        .selectAll('path')
        .data(d => d.values)
        .enter().append('path')
        .attr('d', d => `
          M${this.xScaleBar(d.key)},${scale(d.value) + (d.value <= 2 ? 0 : rad)}
          a${rad},${rad} 0 0 1 ${rad},${d.value <= 2 ? 0 : -rad}
          h${this.xScaleBar.bandwidth() - 2 * rad}
          a${rad},${rad} 0 0 1 ${rad},${d.value <= 2 ? 0 : rad}
          v${this.height - scale(d.value) - (d.value <= 2 ? 0 : rad)}
          h${-(this.xScaleBar.bandwidth())}Z
        `)
        .attr('fill', d => this.colorScale(d.key))
        .style('opacity', this.barsOpacity)
    },
    drawLines () {
      let lines = []
      let scale

      if (this.y1Data && this.y1Data.type === 'line') {
        lines = this.y1Data.data
        scale = this.yScale1
      } else if (this.y2Data && this.y2Data.type === 'line') {
        lines = this.y2Data.data
        scale = this.yScale2
      } else {
        return
      }

      const line = d3.line()
        .curve(d3.curveMonotoneX)
        // .defined(d => d.value)
        .x(d => this.xScaleGroup(d.date))
        .y(d => scale(d.value))

      this.g.append('g')
        .attr('transform', `translate(${this.margin.left + (this.xScaleGroup.bandwidth() / 2)}, 0)`)
        .attr('class', 'lines')
        .style('pointer-events', 'none')

      this.parseLinesData(lines).map((item) => {
        const _line = this.g.select('.lines')
          .append('g')
          .attr('class', 'line')

        _line.append('path')
          .datum(item.values)
          .attr('fill', 'none')
          .attr('stroke', this.colorScale(item.key))
          .attr('stroke-width', '1.5')
          .attr('d', line)

        _line.selectAll('circle').data(item.values).enter()
          .append('circle')
          .attr('data-index', (d) => this.xScaleGroup(d.date))
          .attr('cx', d => this.xScaleGroup(d.date))
          .attr('cy', d => scale(d.value))
          .attr('fill', this.colorScale(item.key))
          .attr('r', (d, i, list) => list.length - 1 === i ? 6 : 0)
      })
    },
    drawLegend () {
      this.legendItems = this.keys.map(key => {
        return {
          name: key,
          color: this.colorScale(key)
        }
      })
    },
    parseLinesData (lines) {
      const maxNumKeys = d3.max(lines || [], d => d.values.length)
      const maxGroup = lines.find(l => l.values.length === maxNumKeys)
      const keys = maxGroup.values.map(v => v.key)
      const lineData = {}
      for (const group of lines) {
        for (const key of keys) {
          lineData[key] = lineData[key] || {
            key, values: []
          }
          const value = group.values.find(v => v.key === key)
          lineData[key].values.push({
            date: group.date,
            value: value ? value.value : 0
          })
        }
      }
      return Object.values(lineData)
    },
    showTooltip (d, xScale, element) {
      this.hoverEffects(xScale(d.date), element)
      const date = d.date
      const bars = this.bars.find(b => {
        return b.date.getTime() === date.getTime()
      })
      const lines = this.lines.find(b => {
        return b.date.getTime() === date.getTime()
      })

      this.tooltipData = []
      if (bars) {
        this.tooltipData = this.tooltipData.concat(bars.values.map(item => {
          return {
            color: this.colorScale(item.key),
            label: item.key,
            value: item.value
          }
        }))
      }
      if (lines) {
        this.tooltipData = this.tooltipData.concat(lines.values.map(item => {
          return {
            color: this.colorScale(item.key),
            label: item.key,
            value: item.value
          }
        }))
      }
      this.tooltipData.date = this.dateFormatter(date)
      if (this.tooltipData.length) {
        this.$refs.tooltip.style.opacity = 1
      }
      this.$refs.tooltip.style.left = `${xScale(date) - xScale.bandwidth() - (!this.y2Data ? 12 : 0)}px`
    },
    hoverEffects (index, element) {
      d3.select(element).select('.guide-line').attr('stroke', '#e4e5e8')
      d3.select(this.$refs.svg).selectAll(`circle[data-index="${index}"`).attr('r', 6)
      // d3.select(this.$refs.svg).selectAll(`circle:not([data-index="${index}"]):last-child`).attr('r', 0)
    },
    hideTooltip (index, element) {
      this.$refs.tooltip.style.opacity = 0
      d3.select(element).select('.guide-line').attr('stroke', 'transparent')
      d3.select(this.$refs.svg).selectAll(`circle:not([data-index="${index}"]):last-child`).attr('r', 6)

      if (this.data.length - 1 !== index) {
        d3.select(this.$refs.svg).selectAll(`circle[data-index="${index}"]:not(:last-child)`).attr('r', 0)
      }
    }
  }
}
